<script setup>
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue'
import { useLanguage } from '@composables/useLanguage'
import 'cloudinary-video-player/cld-video-player.min.css'

const props = defineProps({
  publicId: {
    type: String,
    required: true,
  },
  cloudName: {
    type: String,
    default: () => import.meta.env.VITE_CLOUDINARY_CLOUD_NAME || 'snapkit',
  },
  autoplay: {
    type: Boolean,
    default: true,
  },
  loop: {
    type: Boolean,
    default: true,
  },
  muted: {
    type: Boolean,
    default: true,
  },
  controls: {
    type: Boolean,
    default: true,
  },
})

const { t } = useLanguage()

// State
const videoContainer = ref(null)
const isLoading = ref(true)
const hasError = ref(false)
const errorMessage = ref('')
let playerInstance = null

// Theme detection
const isDark = computed(() => document.documentElement.classList.contains('dark'))

// Get primary color from CSS variables
const primaryColor = computed(() => {
  const root = document.documentElement
  const themeColor = getComputedStyle(root).getPropertyValue('--theme-primary-color')

  if (themeColor) return themeColor.trim()

  // Fallback to purple colors matching the design
  return isDark.value ? '#a78bfa' : '#7c3aed'
})

// Video player configuration
const videoPlayerConfig = computed(() => ({
  cloud_name: props.cloudName,
  secure: true,
  controls: props.controls,
  autoplay: props.autoplay,
  loop: props.loop,
  muted: props.muted,
  fluid: true,
  aspectRatio: '16:9',
  bigPlayButton: true,
  // Disable plugins that cause loading errors
  showLogo: false,
  logoImageUrl: '',
  logoOnclickUrl: '',
  analytics: false,
  aiHighlightsGraph: false,
  playlistWidget: false,
  floatingWhenNotVisible: false,
  hideContextMenu: true,
  // Minimal control bar
  controlBar: {
    playToggle: true,
    volumePanel: false,
    currentTimeDisplay: false,
    timeDivider: false,
    durationDisplay: false,
    progressControl: false,
    liveDisplay: false,
    seekToLive: false,
    remainingTimeDisplay: false,
    customControlSpacer: false,
    playbackRateMenuButton: false,
    chaptersButton: false,
    descriptionsButton: false,
    subsCapsButton: false,
    audioTrackButton: false,
    fullscreenToggle: false,
  },
  // Don't set colors via config (causes plugin loading error)
  // Will style via CSS instead
}))

// Initialize video player
const initializePlayer = async () => {
  if (!videoContainer.value) {
    if (import.meta.env.DEV) {
      console.log('[Video Player] Container not ready')
    }
    return
  }

  if (!props.publicId) {
    if (import.meta.env.DEV) {
      console.log('[Video Player] Waiting for publicId...')
    }
    return
  }

  try {
    if (import.meta.env.DEV) {
      console.log('[Video Player] Initializing player for:', props.publicId)
    }

    isLoading.value = true
    hasError.value = false

    // Wait for DOM to be ready
    await nextTick()

    // Generate static Cloudinary thumbnail for poster
    const posterUrl = `https://res.cloudinary.com/${props.cloudName}/video/upload/w_800,h_450,c_fill,q_auto,f_auto/${props.publicId}.jpg`

    // Create video element
    const videoElement = document.createElement('video')
    videoElement.className = 'cld-video-player w-full h-full'
    videoElement.controls = props.controls
    videoElement.playsInline = true
    videoElement.muted = props.muted
    videoElement.poster = posterUrl

    // Clear container and append video element
    videoContainer.value.innerHTML = ''
    videoContainer.value.appendChild(videoElement)

    if (import.meta.env.DEV) {
      console.log('[Video Player] Video element created, loading player library...')
    }

    // Dynamically import the player to avoid SSR issues
    const { videoPlayer } = await import('cloudinary-video-player')

    if (import.meta.env.DEV) {
      console.log('[Video Player] Player library loaded, initializing...')
    }

    // Initialize player
    playerInstance = videoPlayer(videoElement, videoPlayerConfig.value)

    // Load video source - wrap in try/catch to suppress plugin errors
    try {
      playerInstance.source(props.publicId, {
        sourceTypes: ['mp4', 'webm'],
        transformation: {
          quality: 'auto',
          fetch_format: 'auto',
        },
        info: {
          title: '',
          subtitle: '',
        },
      })
    } catch (pluginError) {
      // Suppress plugin initialization errors (vttThumbnails, etc)
      // Static poster will still display correctly
      if (import.meta.env.DEV) {
        console.warn('[Video Player] Plugin error suppressed:', pluginError.message)
      }
    }

    // Explicitly disable thumbnail plugin if it exists
    if (playerInstance.cloudinary && playerInstance.cloudinary.vttThumbnails) {
      try {
        playerInstance.cloudinary.vttThumbnails.disable()
      } catch (e) {
        // Ignore if plugin doesn't exist
      }
    }

    // Event listeners
    playerInstance.on('loadeddata', () => {
      isLoading.value = false

      if (import.meta.env.DEV) {
        console.log('[Video Player] Loaded successfully:', props.publicId)
      }
    })

    playerInstance.on('error', (e) => {
      console.error('[Video Player] Error:', e)
      hasError.value = true
      errorMessage.value = t('media.error')
      isLoading.value = false
    })
  } catch (error) {
    console.error('[Video Player] Initialization error:', error)
    hasError.value = true
    errorMessage.value = t('media.error')
    isLoading.value = false
  }
}

// Cleanup
const cleanup = () => {
  if (playerInstance) {
    try {
      playerInstance.dispose()
      playerInstance = null
    } catch (error) {
      console.error('[Video Player] Cleanup error:', error)
    }
  }
}

// Lifecycle
onMounted(async () => {
  if (import.meta.env.DEV) {
    console.log('[Video Player] Component mounted')
    console.log('[Video Player] publicId:', props.publicId)
    console.log('[Video Player] videoContainer.value:', videoContainer.value)
  }

  // Wait for DOM to be fully rendered
  await nextTick()

  if (import.meta.env.DEV) {
    console.log('[Video Player] After nextTick, container:', videoContainer.value)
  }

  // Try to initialize (will return early if publicId is empty)
  if (props.publicId) {
    initializePlayer()
  } else {
    if (import.meta.env.DEV) {
      console.log('[Video Player] Will wait for publicId via watch()')
    }
  }
})

onUnmounted(() => {
  cleanup()
})

// Watch for publicId changes - trigger initialization when publicId becomes available
watch(
  () => props.publicId,
  async (newId, oldId) => {
    if (import.meta.env.DEV) {
      console.log('[Video Player] publicId changed:', { oldId, newId })
    }

    // Initialize when publicId changes from empty to actual value
    if (newId && newId !== oldId) {
      cleanup()

      // Wait for DOM to be fully ready before initializing
      await nextTick()

      // Double-check container is ready after nextTick
      if (!videoContainer.value) {
        if (import.meta.env.DEV) {
          console.warn('[Video Player] Container still not ready after nextTick, retrying...')
        }

        // Retry after a short delay
        setTimeout(() => {
          initializePlayer()
        }, 100)
      } else {
        initializePlayer()
      }
    }
  }
)

// Note: Theme colors are applied via CSS :deep() selectors
// No need to reinitialize player on theme change
</script>

<template>
  <div class="cloudinary-video-container w-full relative min-h-[300px]">
    <!-- Loading Overlay (shown over container) -->
    <div
      v-if="isLoading"
      class="absolute inset-0 flex flex-col items-center justify-center bg-gray-100 dark:bg-gray-800 rounded-lg z-10"
    >
      <div class="animate-spin rounded-full h-12 w-12 border-b-4 border-purple-600 dark:border-purple-400 mb-4"></div>
      <p class="text-gray-600 dark:text-gray-400">{{ t('media.loading') }}</p>
    </div>

    <!-- Error Overlay -->
    <div
      v-if="hasError"
      class="absolute inset-0 flex flex-col items-center justify-center p-8 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800 z-10"
    >
      <svg
        class="w-16 h-16 text-red-500 dark:text-red-400 mb-4"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <circle cx="12" cy="12" r="10" stroke-width="2" />
        <line x1="12" y1="8" x2="12" y2="12" stroke-width="2" />
        <line x1="12" y1="16" x2="12.01" y2="16" stroke-width="2" />
      </svg>
      <p class="text-red-600 dark:text-red-400 font-medium">{{ errorMessage }}</p>
    </div>

    <!-- Video Player Container - Always rendered so ref is always available -->
    <div
      ref="videoContainer"
      class="video-player-wrapper w-full rounded-lg overflow-hidden bg-black shadow-lg"
      :class="{ 'opacity-0': isLoading || hasError }"
    ></div>
  </div>
</template>

<style scoped>
.cloudinary-video-container {
  @apply w-full;
}

.video-player-wrapper {
  @apply w-full relative;
  min-height: 300px;
}

/* Cloudinary Video Player Theme Overrides */
.video-player-wrapper :deep(.vjs-control-bar) {
  background-color: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(8px);
}

.dark .video-player-wrapper :deep(.vjs-control-bar) {
  background-color: rgba(17, 24, 39, 0.9);
}

.video-player-wrapper :deep(.vjs-big-play-button) {
  background-color: var(--theme-primary-color, #7c3aed);
  border: none;
  border-radius: 50%;
  width: 80px;
  height: 80px;
  line-height: 80px;
  font-size: 40px;
  transition: all 200ms ease;
}

.video-player-wrapper :deep(.vjs-big-play-button:hover) {
  background-color: var(--theme-primary-hover, #6d28d9);
  transform: scale(1.1);
}

.video-player-wrapper :deep(.vjs-play-control) {
  color: #ffffff;
}

.video-player-wrapper :deep(.vjs-play-control:hover) {
  color: var(--theme-primary-color, #7c3aed);
}

/* Loading spinner inside player */
.video-player-wrapper :deep(.vjs-loading-spinner) {
  border-color: var(--theme-primary-color, #7c3aed);
}

/* Progress bar */
.video-player-wrapper :deep(.vjs-progress-control) {
  display: none;
}

/* Hide unwanted controls */
.video-player-wrapper :deep(.vjs-volume-panel),
.video-player-wrapper :deep(.vjs-current-time),
.video-player-wrapper :deep(.vjs-duration),
.video-player-wrapper :deep(.vjs-fullscreen-control) {
  display: none !important;
}

/* Responsive adjustments */
@media (max-width: 640px) {
  .video-player-wrapper :deep(.vjs-big-play-button) {
    width: 60px;
    height: 60px;
    line-height: 60px;
    font-size: 30px;
  }
}
</style>
